// src/pages/Enseignants/Palmares/PalmaresPage.jsx
import React, { useState, useEffect } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import {
  getAllPalmares,
  deletePalmares,
  togglePalmaresIsActive,
} from "./../../../services/palmaresServices";
import { getSettings } from "./../../../services/settingsServices";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import EditPalmaresModal from "./EditPalmaresModal";
import CreatePalmaresModal from "./CreatePalmaresModal";
import Button from "../../../components/Button/Button";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { FaSearch, FaPlus } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

/* Petit switch accessible pour l’affichage accueil */
function VisibilitySwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition
        ${checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
      title={checked ? "Affiché sur l’accueil" : "Masqué de l’accueil"}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition
          ${checked ? "translate-x-5" : "translate-x-1"}`}
      />
    </button>
  );
}

const PalmaresPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [palmaresData, setPalmaresData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPalmares, setCurrentPalmares] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  // Helpers saisons
  const getPreviousSeason = (currentSeason) => {
    const [startYear, endYear] = String(currentSeason).split("-").map(Number);
    return `${startYear - 1}-${endYear - 1}`;
  };
  const getNextSeason = (currentSeason) => {
    const [startYear, endYear] = String(currentSeason).split("-").map(Number);
    return `${startYear + 1}-${endYear + 1}`;
  };
  const isWithinSeason = (date, season) => {
    if (!season) return true;
    const [startYear, endYear] = season.split("-").map(Number);
    const seasonStart = new Date(`${startYear}-08-01T00:00:00`);
    const seasonEnd = new Date(`${endYear}-07-31T23:59:59`);
    const d = new Date(date);
    return d >= seasonStart && d <= seasonEnd;
  };

  // Chargement
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [palm, settingsData] = await Promise.all([
          getAllPalmares(),
          getSettings(),
        ]);

        setPalmaresData(Array.isArray(palm) ? palm : []);
        const active = settingsData?.[0]?.saison_active || "";
        setSelectedSeason(active);
        if (active) {
          setSeasons([getPreviousSeason(active), active, getNextSeason(active)]);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Erreur lors de la récupération des données");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filtres
  const filteredPalmares = (palmaresData || []).filter((p) => {
    const nom = p?.adherent?.nom || "";
    const prenom = p?.adherent?.prenom || "";
    const matchesSearch =
      `${nom} ${prenom}`.toLowerCase().includes(searchTerm.trim().toLowerCase());
    const matchesSeason =
      !selectedSeason || isWithinSeason(p?.date_palmares, selectedSeason);
    return matchesSearch && matchesSeason;
  });

  // Actions
  const handleEditClick = (palmares) => {
    setCurrentPalmares(palmares);
    setShowEditModal(true);
  };
  const handleDeleteClick = async (id) => {
    try {
      await deletePalmares(id);
      setPalmaresData((prev) => prev.filter((x) => x.id !== id));
      toast.success("Palmarès supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression du palmarès:", error);
      toast.error("Erreur lors de la suppression du palmarès");
    }
  };
  const handleToggleIsActive = async (id) => {
    try {
      const result = await togglePalmaresIsActive(id);
      toast.success(result.message);
      setPalmaresData((prev) =>
        prev.map((p) => (p.id === id ? { ...p, is_active: result.is_active } : p))
      );
    } catch {
      toast.error("Erreur lors du changement de visibilité.");
    }
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Enseignant"
          title="Palmarès"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Résultats & distinctions de vos adhérents
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Recherchez par nom, filtrez par saison et gérez l’affichage sur l’accueil.
              </p>

              <div className="mt-5 flex items-center justify-center">
                <Button
                  text="Ajouter un palmarès"
                  icon={FaPlus}
                  onClick={() => setShowCreateModal(true)}
                  className="!rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Recherche par nom / prénom
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Ex : Martin, Alice…"
                    className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Saison
                </label>
                <select
                  name="selectedSeason"
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Compteur */}
            <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
              Résultats affichés : {filteredPalmares.length} / {palmaresData.length}
            </div>
          </div>

          {/* Table / Cards */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Loading skeleton simple */}
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            ) : filteredPalmares.length === 0 ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Aucun palmarès ne correspond à vos filtres.
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-[960px] w-full text-sm">
                    <thead className="bg-slate-50/90 dark:bg-slate-900/70">
                      <tr className="text-left text-slate-700 dark:text-slate-200">
                        <th className="px-4 py-3">Accueil</th>
                        <th className="px-4 py-3">Adhérent</th>
                        <th className="px-4 py-3">Animations & Résultats</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {filteredPalmares.map((p) => {
                        const a = p?.adherent || {};
                        return (
                          <tr key={p.id} className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors">
                            <td className="px-4 py-3">
                              <VisibilitySwitch
                                checked={!!p.is_active}
                                onChange={() => handleToggleIsActive(p.id)}
                              />
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <img
                                  src={toAbsolute(a.photo_url) || "/img/user_avatar.jpg"}
                                  alt={`${a.nom || ""} ${a.prenom || ""}`}
                                  className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                                />
                                <div className="min-w-0">
                                  <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                    {(a.nom || "").toUpperCase()} {a.prenom || ""}
                                  </div>
                                  
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">
                                {p.titre_palmares || "—"}
                              </div>
                              {p.resultats_palmares && (
                                <span className="mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 ring-1 ring-sky-200/70 dark:ring-sky-800/50">
                                  Résultats : {p.resultats_palmares}
                                </span>
                              )}
                              {p.classement_palmares && (
                                <span className="ml-2 mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 ring-1 ring-amber-200/70 dark:ring-amber-800/50">
                                  Classement : {p.classement_palmares}
                                </span>
                              )}
                              {p.observations_palmares && (
                                <div className="mt-1 text-slate-600 dark:text-slate-300">
                                  {p.observations_palmares}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                              {p.date_palmares
                                ? new Date(p.date_palmares).toLocaleDateString()
                                : "—"}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-center items-center gap-2">
                                <ButtonEdit onClick={() => handleEditClick(p)} />
                                <ButtonDelete onClick={() => handleDeleteClick(p.id)} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {filteredPalmares.map((p) => {
                    const a = p?.adherent || {};
                    return (
                      <div
                        key={p.id}
                        className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/90 dark:bg-slate-900/40"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={toAbsolute(a.photo_url) || "/img/user_avatar.jpg"}
                            alt={`${a.nom || ""} ${a.prenom || ""}`}
                            className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                              {(a.nom || "").toUpperCase()} {a.prenom || ""}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {p.date_palmares
                                ? new Date(p.date_palmares).toLocaleDateString()
                                : "—"}
                            </div>
                          </div>
                          <div className="ml-auto">
                            <VisibilitySwitch
                              checked={!!p.is_active}
                              onChange={() => handleToggleIsActive(p.id)}
                            />
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="font-semibold">{p.titre_palmares || "—"}</div>
                          {p.resultats_palmares && (
                            <div className="mt-1 text-xs">
                              <span className="rounded-full px-2 py-0.5 bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                                Résultats : {p.resultats_palmares}
                              </span>
                            </div>
                          )}
                          {p.classement_palmares && (
                            <div className="mt-1 text-xs">
                              <span className="rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                Classement : {p.classement_palmares}
                              </span>
                            </div>
                          )}
                          {p.observations_palmares && (
                            <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                              {p.observations_palmares}
                            </div>
                          )}
                        </div>

                        <div className="mt-3 flex justify-end gap-2">
                          <ButtonEdit onClick={() => handleEditClick(p)} />
                          <ButtonDelete onClick={() => handleDeleteClick(p.id)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />

      {/* Modales */}
      {showEditModal && (
        <EditPalmaresModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          palmares={currentPalmares}
          refreshList={async () => {
            const data = await getAllPalmares();
            setPalmaresData(Array.isArray(data) ? data : []);
          }}
        />
      )}
      {showCreateModal && (
        <CreatePalmaresModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          refreshList={async () => {
            const data = await getAllPalmares();
            setPalmaresData(Array.isArray(data) ? data : []);
          }}
        />
      )}
    </section>
  );
};

export default PalmaresPage;
